/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
/* eslint-disable no-prototype-builtins */
import { isSkipValue, parseGradingValue, formatDerivedMeasurement } from '../../../../utils/calculationHelpers';

/**
 * Formats a derived measurement, keeping as many decimals as the values it came from so a
 * grading of '1.500' does not degrade to 0.5.
 * @param {number} num computed measurement
 * @param {...(string|number|null|undefined)} sources raw inputs it was derived from
 * @returns {string}
 */
const formatNumber = (num, ...sources) => {
  if (Number.isNaN(num)) return '';
  return formatDerivedMeasurement(num, ...sources);
};

export async function calculationSampleSize(sampleData, orgSampleData) {
  let sampleIndex = null;
  const sampleObj = sampleData.gradings?.find((item, idx) => {
    if (item.isZero || item.isSampleSize) sampleIndex = idx;
    return item.isZero || item.isSampleSize;
  });
  const orgSampleObj = orgSampleData?.gradings?.find((item) => item.isZero || item.isSampleSize);
  const sampleMeasurement = isSkipValue(sampleObj?.measurment) ? 0 : parseFloat(sampleObj?.measurment) || 0;
  const orgSampleMeasurement = isSkipValue(orgSampleObj?.measurment) ? 0 : parseFloat(orgSampleObj?.measurment) || 0;

  sampleData.gradings?.forEach((grading, idx) => {
    if (!grading.hasOwnProperty('grading')) {
      grading.isUserChanged = parseFloat(sampleMeasurement) !== parseFloat(orgSampleMeasurement) || grading.isUserChanged;
      return;
    }
    if (isSkipValue(grading.grading)) {
      grading.measurment = grading.grading;
      return;
    }

    /**
     * `shouldAdd` carries the direction the rule's sign encodes, keeping this the exact inverse of
     * the server's deriveGradingsFromMeasurements, which runs whenever the base size moves.
     * Position alone cannot express a size left of the base that measures LARGER than it (or one
     * to the right that measures smaller), so deriving direction from the column silently inverted
     * those rows. An unsigned rule still follows its position, leaving the ordinary ascending size
     * run byte-identical (BM-9510).
     */
    const { value: gradingValue, shouldAdd } = parseGradingValue(grading.grading, idx < sampleIndex);

    grading.isUserChanged = parseFloat(sampleMeasurement) !== parseFloat(orgSampleMeasurement) || grading.isUserChanged;

    if (idx !== sampleIndex) {
      const base = parseFloat(sampleMeasurement) || 0;
      const newMeasurement = shouldAdd ? base + gradingValue : base - gradingValue;
      grading.measurment = Number.isNaN(newMeasurement) ? '' : formatDerivedMeasurement(newMeasurement, grading.grading, sampleObj?.measurment);
    }
  });
  return sampleData;
}

export async function calculatePreviousSize(sampleData, orgSampleData) {
  let sampleIndex = null;
  const sampleObj = sampleData.gradings?.find((item, idx) => {
    if (item.isZero || item.isSampleSize) sampleIndex = idx;
    return item.isZero || item.isSampleSize;
  });
  const orgSampleObj = orgSampleData?.gradings?.find((item) => item.isZero || item.isSampleSize);
  const sampleMeasurement = isSkipValue(sampleObj?.measurment) ? 0 : parseFloat(sampleObj?.measurment) || 0;
  const orgSampleMeasurement = isSkipValue(orgSampleObj?.measurment) ? 0 : parseFloat(orgSampleObj?.measurment) || 0;

  let currentMeasurement = sampleMeasurement;
  for (let i = sampleIndex - 1; i >= 0; i--) {
    const gradingItem = sampleData?.gradings[i];
    if (gradingItem?.hasOwnProperty('grading')) {
      if (isSkipValue(gradingItem.grading)) {
        gradingItem.measurment = gradingItem.grading;
        continue;
      }

      /**
       * Walking left, away from the base: the sign says whether this step adds or subtracts. The
       * skip-token branch that stood here was unreachable — the loop already continues on a skip
       * token above — and was the only caller of findLastValidMeasurement, now removed with it.
       */
      const { value: gradingValue, shouldAdd } = parseGradingValue(gradingItem.grading, true);
      currentMeasurement = shouldAdd ? currentMeasurement + gradingValue : currentMeasurement - gradingValue;

      gradingItem.measurment = formatNumber(currentMeasurement, gradingItem.grading, sampleObj?.measurment);
      gradingItem.isUserChanged = sampleMeasurement !== orgSampleMeasurement || gradingItem.isUserChanged;
    }
  }

  currentMeasurement = sampleMeasurement;

  for (let i = sampleIndex + 1; i < sampleData?.gradings?.length; i++) {
    const gradingItem = sampleData?.gradings[i];
    if (gradingItem?.hasOwnProperty('grading')) {
      if (isSkipValue(gradingItem?.grading)) {
        gradingItem.measurment = gradingItem?.grading;
        continue;
      }

      /**
       * Walking right, away from the base. The running total is NOT passed through Math.abs: a
       * cumulative chain that legitimately crosses zero was folded back to positive, reporting 14
       * where the measurement is -4 and inverting every later step of the walk (BM-9510).
       */
      const { value: gradingValue, shouldAdd } = parseGradingValue(gradingItem?.grading, false);
      currentMeasurement = shouldAdd ? currentMeasurement + gradingValue : currentMeasurement - gradingValue;

      gradingItem.measurment = formatNumber(currentMeasurement, gradingItem.grading, sampleObj?.measurment);
      gradingItem.isUserChanged = sampleMeasurement !== orgSampleMeasurement || gradingItem.isUserChanged;
    }
  }

  if (sampleObj) {
    sampleObj.measurment = formatNumber(sampleMeasurement, sampleObj?.measurment);
    sampleObj.isUserChanged = sampleMeasurement !== orgSampleMeasurement || sampleObj.isUserChanged;
  }
}
